import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  Text,
  Separator,
  Select,
  Breadcrumb,
  DatePicker,
  Loader,
} from '@/ui'
import { TypographyStyles } from '@/styles'

import { Dimmer } from 'semantic-ui-react'

import { adminFetchSingleUserIfNeeded } from '@/actions/admin/adminAllUsersActions'
import { fetchUserFinancialAccounts } from '@/actions/admin/adminFinancialAccountActions'

import { getUserFinancialAccounts } from '@/selectors/financeSelectors'
import { getUserById } from '@/selectors/user.selectors'
import { useReselector, useToggle } from '@/utils/sharedHooks'
import { filterObjNils, useAppDispatch } from '@/utils/typeHelpers'
import { getAllTransactionCategories } from '@/features/reports/reports.selectors'
import { fetchAdminTransactionCategoriesIfNeeded } from '@/features/reports/reports.slice'
import { DateTime } from 'luxon'

import {
  downloadGeneralLedgerCsv,
  fetchGeneralLedger,
  FETCH_GENERAL_LEDGER_KEY,
} from '@/actions/admin/journalEntryActions'

import {
  ACCOUNT_TYPE_OPTIONS,
  FINANCIAL_STATEMENT_OPTIONS,
  GL_SORT_OPTIONS,
} from '../reportConstants'
import { selectIsFetchingForKeys } from '@/reducers/fetch'

import { GeneralLedger } from '@/features/journal-entries/components/GeneralLedger'
import { getInitialDateFilters } from '@/features/journal-entries/service'
import { LedgerAccount } from '@/features/journal-entries/types'
import { EditJournalEntryModal } from '@/features/journal-entries/components/EditJournalEntryModal'
export const AdminGeneralLedgerPanel = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [generalLedgerItems, setGeneralLedgerItems] = useState<LedgerAccount[]>(
    []
  )
  const [dateRange, setDateRange] = useState<[string, string]>(
    getInitialDateFilters(searchParams?.get('endDate'))
  )
  const [accountFilter, setAccountFilter] = useState<string | undefined>()
  const [statementFilter, setStatementFilter] = useState<string | undefined>()
  const [sortBy, setSortBy] = useState('A-Z')

  const [isCreating, toggleCreate] = useToggle()

  const { userId: userIdParam } = useParams<{ userId: string }>()
  const userId = Number(userIdParam)

  const user = useReselector(getUserById, userId)
  const transactionCategoriesById = useReselector(getAllTransactionCategories)
  const financialAccountsObj = useReselector(getUserFinancialAccounts, userId)

  const queryLoading = useReselector(selectIsFetchingForKeys, [
    FETCH_GENERAL_LEDGER_KEY,
  ])

  const financialAccountsById = useMemo(
    () => filterObjNils(financialAccountsObj),
    [financialAccountsObj]
  )
  const usersLink = '/admin/finances/list-users'

  useEffect(() => {
    const fetch = async () => {
      const res = await fetchGeneralLedger({
        userId,
        startDate: dateRange[0],
        endDate: dateRange[1],
      })(dispatch)
      setGeneralLedgerItems(res)
    }
    if (dateRange?.[0] && dateRange?.[1] && isCreating === false) {
      fetch()
    } else {
      setGeneralLedgerItems([])
    }
  }, [dispatch, dateRange, userId, isCreating])

  useEffect(() => {
    dispatch(adminFetchSingleUserIfNeeded(userId))
    dispatch(fetchUserFinancialAccounts(userId))
    dispatch(fetchAdminTransactionCategoriesIfNeeded(true))
  }, [dispatch, userId])

  const handleDateChange = (dateRange: [string, string]) => {
    setDateRange(dateRange)
  }
  const handleDownloadCsv = async () => {
    setIsLoading(true)
    try {
      const blob = await downloadGeneralLedgerCsv({
        userId,
        startDate: dateRange[0],
        endDate: dateRange[1],
      })(dispatch)
      if (blob) {
        const url = URL.createObjectURL(new Blob([blob], { type: 'text/csv' }))
        const link = document.createElement('a')
        const startDateStr =
          DateTime.fromISO(dateRange[0]).toISODate() ?? dateRange[0]
        const endDateStr =
          DateTime.fromISO(dateRange[1]).toISODate() ?? dateRange[1]
        link.href = url
        link.download = `general_ledger_${startDateStr}_${endDateStr}.csv`
        link.click()
        URL.revokeObjectURL(url)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <Breadcrumb>
        <Breadcrumb.List>
          <Breadcrumb.Item>
            <Breadcrumb.Link onClick={() => navigate('/')}>
              Dashboard
            </Breadcrumb.Link>
          </Breadcrumb.Item>
          <Breadcrumb.Separator />
          <Breadcrumb.Item>
            <Breadcrumb.Link onClick={() => navigate(usersLink)}>
              Users
            </Breadcrumb.Link>
          </Breadcrumb.Item>
          <Breadcrumb.Separator />
          <Breadcrumb.Item>
            <Breadcrumb.Link
              onClick={() => navigate(`/admin/finances/records/${userId}`)}
            >
              {`${user?.firstName} ${user?.lastName}`}
            </Breadcrumb.Link>
          </Breadcrumb.Item>
          <Breadcrumb.Separator />
          <Breadcrumb.Item>
            <Breadcrumb.Page>General Ledger</Breadcrumb.Page>
          </Breadcrumb.Item>
        </Breadcrumb.List>
      </Breadcrumb>
      <Separator />
      <Card>
        <div className="flex flex-wrap">
          <div className="w-full md:w-2/3 lg:w-8/12 flex flex-col">
            <Text variant={TypographyStyles.H1}>General Ledger</Text>
            <Text variant={TypographyStyles.H3}>
              {user?.financialProfile?.businessName ??
                `${user?.firstName} ${user?.lastName}`}
            </Text>
          </div>
          <div className="w-full md:w-1/3 lg:w-4/12 flex flex-col gap-2">
            <Button
              variant="secondary"
              onClick={toggleCreate}
              className="w-full"
            >
              Create Journal Entry
            </Button>
            {isCreating && (
              <EditJournalEntryModal open={isCreating} onClose={toggleCreate} />
            )}
            <Button
              loading={isLoading}
              disabled={!generalLedgerItems.length}
              onClick={handleDownloadCsv}
              className="w-full text-right"
            >
              Download CSV
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap mt-4 mb-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 justify-between w-full">
            <DatePicker
              clearable
              onChange={handleDateChange}
              endDate={dateRange[1]}
              selectsRange
              startDate={dateRange[0]}
              value={dateRange}
              placeholder="Filter Date"
              className="w-full"
            />
            <div className="w-full">
              <Select
                placeholder="Filter account type"
                value={accountFilter}
                withTrigger
                options={ACCOUNT_TYPE_OPTIONS}
                onValueChange={(value) => setAccountFilter(value)}
                clearable
              />
            </div>
            <div className="w-full ">
              <Select
                placeholder="Filter financial statement"
                value={statementFilter}
                options={FINANCIAL_STATEMENT_OPTIONS}
                onValueChange={(value) => setStatementFilter(value)}
                clearable
              />
            </div>
            <div className="w-full">
              <Select
                placeholder={`Sort by: ${sortBy}`}
                value={sortBy}
                options={GL_SORT_OPTIONS}
                onValueChange={(value) => setSortBy(value ?? '')}
                variant="text"
                clearable
              />
            </div>
          </div>
        </div>
        {queryLoading ? (
          <Dimmer active inverted>
            <Loader>Loading general ledger data</Loader>
          </Dimmer>
        ) : (
          <GeneralLedger
            generalLedgerItems={generalLedgerItems}
            transactionCategoriesById={transactionCategoriesById ?? {}}
            financialAccountsById={financialAccountsById ?? {}}
            accountFilter={accountFilter}
            statementFilter={statementFilter}
            sortBy={sortBy}
          />
        )}
      </Card>
    </div>
  )
}
